import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { FaArrowRight, FaCode, FaLaptopCode } from "react-icons/fa";

const CTASection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  
  const isSoftwareDevelopmentPage = location.pathname === "/software-development";
  
  const handleStartProjectClick = (e) => {
    if (isSoftwareDevelopmentPage) {
      e.preventDefault();
      document.getElementById('software-contact').scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const handleExploreServicesClick = (e) => {
    if (isSoftwareDevelopmentPage) {
      e.preventDefault();
      document.getElementById('software-services').scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <div className="bg-gradient-to-r from-black to-gray-900 py-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden"
        >
          <div className="md:flex items-stretch">
            {/* Left side content */}
            <div className={`md:w-2/3 p-8 md:p-12 flex flex-col justify-center ${isRTL ? 'text-right' : ''}`}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t("Ready to Transform Your")} <span className="text-primary">{t("Digital Presence")}</span>?
              </h2>
              <p className="text-gray-400 mb-8 max-w-2xl">
                {t("Our team of expert developers is ready to bring your vision to life. Whether you need a custom web application, mobile app, or enterprise solution, we have the skills and experience to deliver exceptional results.")}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to={isSoftwareDevelopmentPage ? "#software-contact" : "/contact"}
                  onClick={handleStartProjectClick}
                  className="inline-flex items-center bg-primary text-black px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors"
                >
                  {t("Start Your Project")}
                  <FaArrowRight className={isRTL ? 'mr-2 transform rotate-180' : 'ml-2'} />
                </Link>
                
                <Link
                  to={isSoftwareDevelopmentPage ? "#software-services" : "/services"}
                  onClick={handleExploreServicesClick}
                  className="inline-flex items-center bg-transparent border-2 border-gray-700 text-white px-6 py-3 rounded-lg font-bold hover:border-primary hover:text-primary transition-colors"
                >
                  {t("Explore Services")}
                </Link>
              </div>
            </div>
            
            {/* Right side decorative elements */}
            <div className="hidden md:block md:w-1/3 bg-gray-800 relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 text-primary text-9xl">
                  <FaCode />
                </div>
                <div className="absolute bottom-10 right-10 text-primary text-9xl">
                  <FaLaptopCode />
                </div>
              </div>
              
              {/* Animated dots pattern */}
              <div className="absolute inset-0">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-primary rounded-full"
                    style={{
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      opacity: [0.2, 1, 0.2],
                      scale: [1, 1.5, 1],
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 5,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Stats section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className={`text-center ${isRTL ? 'rtl' : ''}`}
          >
            <div className="text-4xl md:text-5xl font-bold text-primary mb-2">50+</div>
            <div className="text-gray-400">{t("Projects Completed")}</div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className={`text-center ${isRTL ? 'rtl' : ''}`}
          >
            <div className="text-4xl md:text-5xl font-bold text-primary mb-2">98%</div>
            <div className="text-gray-400">{t("Client Satisfaction")}</div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className={`text-center ${isRTL ? 'rtl' : ''}`}
          >
            <div className="text-4xl md:text-5xl font-bold text-primary mb-2">15+</div>
            <div className="text-gray-400">{t("Expert Developers")}</div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className={`text-center ${isRTL ? 'rtl' : ''}`}
          >
            <div className="text-4xl md:text-5xl font-bold text-primary mb-2">24/7</div>
            <div className="text-gray-400">{t("Support & Maintenance")}</div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CTASection;
